(() => {
  if (window.FleximaaPackCartInitialized) return;
  window.FleximaaPackCartInitialized = true;

  const PACK_ID = '_Fleximaa pack ID';
  const PACK_SIZE = 'Build Your Pack';
  const SOURCE_HANDLE = '_Fleximaa source handle';
  const COLOUR = '_Fleximaa colour';
  const BUILDER_HANDLE = '_Fleximaa builder handle';
  const BUILDER_TITLE = '_Fleximaa builder title';
  const ROOT = window.Shopify?.routes?.root || '/';

  class FleximaaPackCartManager {
    constructor() {
      this.cart = null;
      this.groups = new Map();
      this.renderTimer = null;
      this.dialog = null;
      this.busy = false;
      this.builderCache = new Map();
      this.bindEvents();
      this.scheduleRender();
    }

    bindEvents() {
      document.addEventListener('click', (event) => {
        const editButton = event.target.closest('[data-fleximaa-pack-edit]');
        const removeButton = event.target.closest('[data-fleximaa-pack-remove]');

        if (editButton) this.openEditor(editButton.dataset.fleximaaPackEdit);
        if (removeButton) this.removePack(removeButton.dataset.fleximaaPackRemove);
      });

      if (window.subscribe && window.PUB_SUB_EVENTS) {
        window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, () => this.scheduleRender());
      }

      this.observer = new MutationObserver(() => this.scheduleRender());
      this.observeCartChanges();
    }

    scheduleRender() {
      window.clearTimeout(this.renderTimer);
      this.renderTimer = window.setTimeout(() => this.render(), 80);
    }

    async render() {
      if (!document.querySelector('cart-items, cart-drawer-items')) return;
      if(this.rendering){this.renderAgain=true;return;}
      this.rendering=true;

      try {
        this.cart = await this.request('cart.js');

      this.groups = this.groupPackItems(this.cart.items || []);
      await Promise.all([...this.groups.values()].map((group) => this.hydrateLegacyGroup(group)));
      this.observer.disconnect();
      document.querySelectorAll('.fleximaa-pack-cart__header').forEach((node) => node.remove());
      document.querySelectorAll('.fa-offer-cart-source').forEach(node=>{node.hidden=false;node.classList.remove('fa-offer-cart-source');});
      document.querySelectorAll('.fleximaa-pack-cart__line').forEach((node) => {
        node.classList.remove(
          'fleximaa-pack-cart__line',
          'fleximaa-pack-cart__line--last',
          'fleximaa-pack-cart--comfortable',
          'fleximaa-pack-cart--balanced',
          'fleximaa-pack-cart--compact'
        );
        node.removeAttribute('data-fleximaa-pack-id');
      });

      this.groups.forEach((group) => this.decorateGroup(group));
      this.updateDisplayedSubtotal();
      this.observeCartChanges();
      } catch(error){this.observeCartChanges();}
      finally{this.rendering=false;if(this.renderAgain){this.renderAgain=false;this.scheduleRender();}}
    }

    observeCartChanges() {
      this.observer.observe(document.body, { childList: true, subtree: true });
    }

    updateDisplayedSubtotal() {
      const items = this.cart?.items || [];
      if (!items.length) return;

      const displayedTotal=this.cart?.total_price;
      if(!Number.isFinite(displayedTotal))return;

      document.querySelectorAll('.totals__subtotal-value,.totals__total-value').forEach((node) => {
        node.textContent = this.money(displayedTotal);
      });
    }

    groupPackItems(items) {
      const groups = new Map();

      items.forEach((item, index) => {
        const id = item.properties?.[PACK_ID];
        if (!id) return;

        if (!groups.has(id)) groups.set(id, { id, items: [], pieces: 0 });
        const group = groups.get(id);
        group.items.push({ ...item, line: index + 1 });
        group.pieces += Number(item.quantity || 0);
      });

      return groups;
    }

    money(cents) {
      const currency = this.cart?.currency || window.Shopify?.currency?.active || 'INR';
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
          style: 'currency',
          currency,
          maximumFractionDigits: 0
        }).format(Number(cents || 0) / 100);
      } catch (error) {
        return `₹${(Number(cents || 0) / 100).toFixed(0)}`;
      }
    }

    optionValue(item, pattern, fallbackIndex) {
      const options = item.options_with_values || [];
      const named = options.find((option) => pattern.test(option.name || ''));
      return named?.value || item.variant_options?.[fallbackIndex] || '';
    }

    cleanBuilderTitle(value) {
      return String(value || '')
        .replace(/\s*[–-]\s*Build Your Pack.*$/i, '')
        .replace(/\s*\(Pick Any[^)]*\)\s*$/i, '')
        .trim();
    }

    async getBuilderContext(builderHandle) {
      if (!builderHandle) throw new Error('Builder handle missing.');
      if (!this.builderCache.has(builderHandle)) {
        this.builderCache.set(builderHandle, (async () => {
          const html = await this.requestText(`products/${builderHandle}`);
          const documentNode = new DOMParser().parseFromString(html, 'text/html');
          const builderNode = documentNode.querySelector('fleximaa-build-your-pack');
          const dataNode = builderNode?.querySelector('[data-fleximaa-pack-products]');
          if (!dataNode) throw new Error('Pack catalogue not found.');
          return {
            title: this.cleanBuilderTitle(builderNode?.dataset.productTitle || ''),
            catalog: this.normalizeCatalog(
              JSON.parse(dataNode.textContent),
              builderHandle === 'womens-plain-round-neck-3-4-sleeve-long-top-build-your-pack-2-4-5-8-10-pieces'
            )
          };
        })());
      }
      return this.builderCache.get(builderHandle);
    }

    async hydrateLegacyGroup(group) {
      const builderHandle = group.items.find((item) => item.properties?.[BUILDER_HANDLE])?.properties?.[BUILDER_HANDLE];
      const needsMetadata = group.items.some((item) => !item.properties?.[COLOUR] || !item.properties?.[BUILDER_TITLE]);
      if (!builderHandle || !needsMetadata) return;

      try {
        const context = await this.getBuilderContext(builderHandle);
        const variantsById = new Map();
        context.catalog.forEach((product) => product.variants.forEach((variant) => {
          variantsById.set(Number(variant.id), { product, variant });
        }));
        group.items = group.items.map((item) => {
          const match = variantsById.get(Number(item.variant_id));
          return {
            ...item,
            properties: {
              ...(item.properties || {}),
              [PACK_SIZE]: item.properties?.[PACK_SIZE] || `${group.pieces} pieces`,
              [SOURCE_HANDLE]: item.properties?.[SOURCE_HANDLE] || match?.product?.handle || '',
              [COLOUR]: item.properties?.[COLOUR] || match?.product?.colour || '',
              [BUILDER_TITLE]: item.properties?.[BUILDER_TITLE] || context.title || ''
            }
          };
        });
      } catch (error) {
        // Keep the cart usable even if a legacy pack cannot be enriched immediately.
      }
    }

    decorateGroup(group) {
      ['CartItem-', 'CartDrawer-Item-'].forEach((prefix) => {
        const rows = group.items
          .map((item) => document.getElementById(`${prefix}${item.line}`))
          .filter(Boolean);

        if (!rows.length) return;
        if(group.items.every(item=>item.properties?.[BUILDER_HANDLE]==='all-offers')){this.decorateOfferGroup(group,rows);return;}

        const density = group.pieces <= 3 ? 'comfortable' : group.pieces <= 6 ? 'balanced' : 'compact';
        const densityClass = `fleximaa-pack-cart--${density}`;

        rows.forEach((row, index) => {
          const item = group.items[index];
          row.classList.add('fleximaa-pack-cart__line', densityClass);
          row.dataset.fleximaaPackId = group.id;
          if (index === rows.length - 1) row.classList.add('fleximaa-pack-cart__line--last');

          row.querySelectorAll('quantity-input, cart-remove-button, .quantity-popover-container').forEach((node) => {
            node.hidden = true;
          });

          row.querySelectorAll('.cart-item__link').forEach((node) => node.remove());
          row.querySelectorAll('a.cart-item__name').forEach((link) => {
            const name = document.createElement('span');
            name.className = link.className;
            name.textContent = link.textContent;
            link.replaceWith(name);
          });

          const details = row.querySelector('.cart-item__details');
          if (details) {
            details.querySelector('.fleximaa-pack-cart__selection')?.remove();
            const color = this.displayColour(item.properties?.[COLOUR] || this.optionValue(item, /colou?r/i, 0));
            const size = this.optionValue(item, /size/i, 0);
            details.insertAdjacentHTML(
              'afterbegin',
              `<div class="fleximaa-pack-cart__selection" aria-label="${this.escape(`${color}, Size ${size}, Quantity ${item.quantity}`)}">
                <strong>${this.escape(color)}</strong>
                <span>Size ${this.escape(size)}</span>
                <span>Qty ${Number(item.quantity)}</span>
              </div>`
            );
          }
        });

        const title = this.cleanBuilderTitle(group.items[0]?.properties?.[BUILDER_TITLE]
          || group.items[0]?.product_title
          || group.items[0]?.product?.title
          || 'Selected items');
        const total = group.items.reduce((sum, item) => sum + Number(item.final_line_price || 0), 0);
        const header = document.createElement('tr');
        header.className = `fleximaa-pack-cart__header ${densityClass}`;
        header.dataset.fleximaaPackId = group.id;
        header.innerHTML = `
          <td colspan="4">
            <div class="fleximaa-pack-cart__heading">
              <div class="fleximaa-pack-cart__identity">
                <strong class="fleximaa-pack-cart__product-title">${this.escape(title)}</strong>
                <span class="fleximaa-pack-cart__eyebrow">Build Your Pack · ${group.pieces} pieces</span>
              </div>
              <div class="fleximaa-pack-cart__summary">
                <strong class="fleximaa-pack-cart__total">${this.money(total)}</strong>
                <div class="fleximaa-pack-cart__actions">
                  <button type="button" data-fleximaa-pack-edit="${this.escape(group.id)}">Edit pack</button>
                  <button type="button" data-fleximaa-pack-remove="${this.escape(group.id)}">Remove pack</button>
                </div>
              </div>
            </div>
          </td>`;
        rows[0].before(header);
      });
    }

    decorateOfferGroup(group,rows){
      if(group.items.some(item=>!Number.isFinite(item.final_line_price)))return;
      const total=group.items.reduce((sum,item)=>sum+item.final_line_price,0);
      const cards=group.items.map(item=>{
        const colour=this.displayColour(item.properties?.[COLOUR]||this.optionValue(item,/colou?r/i,0));
        const size=this.optionValue(item,/size/i,0),image=item.featured_image?.url||item.image||'';
        return `<li class="fa-offer-cart-piece">${image?`<img src="${this.escape(image)}" alt="${this.escape(item.product_title+' — '+colour)}" width="160" height="200" loading="lazy">`:''}<strong>${this.escape(colour)}</strong><span>Size ${this.escape(size)}${item.quantity>1?' · Qty '+Number(item.quantity):''}</span></li>`;
      }).join('');
      const header=document.createElement('tr');header.className='fleximaa-pack-cart__header fa-offer-cart-header';header.dataset.fleximaaPackId=group.id;
      header.innerHTML=`<td colspan="4"><section class="fa-offer-cart-panel" aria-label="All Offers pack"><div class="fa-offer-cart-top"><div><span class="fa-offer-cart-kicker">Your selected pack</span><h2>All Offers</h2><p>${group.pieces} pieces · Colours & sizes you chose</p></div><div class="fa-offer-cart-price"><span>Pack total</span><strong>${this.money(total)}</strong></div></div><ul class="fa-offer-cart-grid" aria-label="Selected pieces">${cards}</ul><div class="fa-offer-cart-footer"><button type="button" data-fleximaa-pack-edit="${this.escape(group.id)}">Edit colours & sizes</button><button type="button" data-fleximaa-pack-remove="${this.escape(group.id)}">Remove pack</button></div></section></td>`;
      rows[0].before(header);rows.forEach(row=>{row.hidden=true;row.classList.add('fa-offer-cart-source');row.dataset.fleximaaPackId=group.id;});
    }

    async openEditor(packId) {
      const group = this.groups.get(packId);
      if (!group || this.busy) return;
      if (group.items.some(item => item.properties?.[BUILDER_HANDLE] === 'all-offers')) {
        window.location.assign(`${ROOT}pages/all-offers?edit_pack=${encodeURIComponent(group.id)}`);
        return;
      }

      let catalog;
      try {
        catalog = await this.loadCatalog(group);
      } catch (error) {
        this.showNotice('The available colours and sizes could not be loaded. Please try again.');
        return;
      }

      if (!catalog.length || catalog.some((product) => product.sizeIndex < 0)) {
        this.showNotice('The connected products no longer have the required Size option.');
        return;
      }

      const variantsById = new Map();
      catalog.forEach((product) => product.variants.forEach((variant) => {
        variantsById.set(Number(variant.id), { product, variant });
      }));
      const selections = [];
      group.items.forEach((item) => {
        const match = variantsById.get(Number(item.variant_id));
        for (let count = 0; count < Number(item.quantity); count += 1) {
          selections.push({
            color: item.properties?.[COLOUR] || match?.product?.colour || '',
            size: match?.variant?.options?.[match.product.sizeIndex] || ''
          });
        }
      });

      const builderHandle = group.items.find((item) => item.properties?.[BUILDER_HANDLE])?.properties?.[BUILDER_HANDLE] || '';
      const builderTitle = this.cleanBuilderTitle(group.items.find((item) => item.properties?.[BUILDER_TITLE])?.properties?.[BUILDER_TITLE] || '');
      this.renderDialog({ group, catalog, selections, builderHandle, builderTitle });
    }

    async loadCatalog(group) {
      const builderHandle = group.items.find((item) => item.properties?.[BUILDER_HANDLE])?.properties?.[BUILDER_HANDLE];
      if (builderHandle) {
        return (await this.getBuilderContext(builderHandle)).catalog;
      }

      const handles = [...new Set(group.items.map((item) =>
        item.properties?.[SOURCE_HANDLE] || item.handle || this.handleFromUrl(item.url)
      ).filter(Boolean))];
      const products = await Promise.all(handles.map((handle) => this.request(`products/${handle}.js`)));
      return this.normalizeCatalog(products);
    }

    normalizeCatalog(products, splitVariantColours = false) {
      return products.flatMap((product) => {
        const optionNames = (product.options || []).map((option) => typeof option === 'string' ? option : option.name);
        const sizeIndex = optionNames.findIndex((name) => /size/i.test(name || ''));
        const colorIndex = optionNames.findIndex((name) => /colou?r/i.test(name || ''));

        if (product.colour && (!splitVariantColours || colorIndex < 0)) return [{ ...product, sizeIndex }];
        if (colorIndex < 0 || sizeIndex < 0) return [];

        const colours = [...new Set(product.variants.map((variant) => variant.options[colorIndex]).filter(Boolean))];
        return colours.map((colour) => ({
          ...product,
          colour,
          sizeIndex,
          variants: product.variants.filter((variant) => variant.options[colorIndex] === colour)
        }));
      });
    }

    renderDialog(state) {
      this.dialog?.remove();
      const wltSizePricing = state.builderHandle === 'womens-plain-round-neck-3-4-sleeve-long-top-build-your-pack-2-4-5-8-10-pieces';
      const sizeLabel = (size) => wltSizePricing && ['3XL', '4XL'].includes(size) ? `${size} (+₹50)` : size;
      const colors = [...new Set(state.catalog.map((product) => product.colour).filter(Boolean))];
      const sizes = [...new Set(state.catalog.flatMap((product) =>
        product.variants.map((variant) => variant.options[product.sizeIndex]).filter(Boolean)
      ))];
      const optionMarkup = (values, selected, placeholder, displayValue = (value) => value) => `
        <option value="">${placeholder}</option>
        ${values.map((value) => `<option value="${this.escape(value)}" ${value === selected ? 'selected' : ''}>${this.escape(displayValue(value))}</option>`).join('')}`;
      const dialogTitle = state.builderTitle || this.cleanBuilderTitle(state.group.items[0]?.product_title) || 'your pack';

      this.dialog = document.createElement('dialog');
      this.dialog.className = 'fleximaa-pack-editor';
      this.dialog.innerHTML = `
        <form method="dialog" class="fleximaa-pack-editor__panel">
          <div class="fleximaa-pack-editor__header">
            <div>
              <span>Build Your Pack · ${state.group.pieces} pieces</span>
              <h2>Edit ${this.escape(dialogTitle)}</h2>
            </div>
            <button class="fleximaa-pack-editor__close" value="cancel" aria-label="Close">&times;</button>
          </div>
          <div class="fleximaa-pack-editor__same-size">
            <label for="FleximaaPackEditorSameSize">Apply one size to all</label>
            <select id="FleximaaPackEditorSameSize">
              ${optionMarkup(sizes, '', 'Mixed sizes', sizeLabel)}
            </select>
          </div>
          <div class="fleximaa-pack-editor__items">
            ${state.selections.map((selection, index) => `
              <article data-fleximaa-editor-item="${index}">
                <strong>Item ${index + 1}</strong>
                <div class="fleximaa-pack-editor__item-controls">
                  ${this.editorPreviewMarkup(state.catalog, selection.color)}
                  <label>Colour<select data-fleximaa-option="color">${optionMarkup(colors, selection.color, 'Select colour', (value) => this.displayColour(value))}</select></label>
                  <label>Size<select data-fleximaa-option="size">${optionMarkup(sizes, selection.size, 'Select size', sizeLabel)}</select></label>
                </div>
                <p hidden></p>
              </article>`).join('')}
          </div>
          <p class="fleximaa-pack-editor__error" role="alert" hidden></p>
          <div class="fleximaa-pack-editor__footer">
            <button class="button button--secondary" value="cancel">Cancel</button>
            <button class="button button--primary" type="button" data-fleximaa-pack-update>Update pack</button>
          </div>
        </form>`;

      document.body.appendChild(this.dialog);
      const sameSizeSelect = this.dialog.querySelector('#FleximaaPackEditorSameSize');
      sameSizeSelect.addEventListener('change', (event) => {
        if (!event.target.value) return;
        this.dialog.querySelectorAll('[data-fleximaa-option="size"]').forEach((select) => { select.value = event.target.value; });
        this.syncEditorSameSize();
        this.validateEditor(state);
      });
      this.dialog.addEventListener('change', (event) => {
        if (event.target.matches('[data-fleximaa-option="color"]')) {
          const row = event.target.closest('[data-fleximaa-editor-item]');
          this.updateEditorPreview(row, state.catalog, event.target.value);
        }
        if (event.target.matches('[data-fleximaa-option="size"]')) this.syncEditorSameSize();
        this.validateEditor(state);
      });
      this.dialog.querySelector('[data-fleximaa-pack-update]').addEventListener('click', () => this.updatePack(state));
      this.syncEditorSameSize();
      this.validateEditor(state);
      this.dialog.showModal();
    }

    catalogPreview(catalog, color) {
      const product = catalog.find((candidate) => candidate.colour === color);
      if (!product) return null;
      const variant = product.variants.find((candidate) => candidate.available && candidate.image)
        || product.variants.find((candidate) => candidate.image);
      return variant?.image ? { image: variant.image, colour: this.displayColour(color) } : null;
    }

    editorPreviewMarkup(catalog, color) {
      const preview = this.catalogPreview(catalog, color);
      return `<span class="fleximaa-pack-editor__preview" data-fleximaa-editor-preview>
        <img src="${preview ? this.escape(preview.image) : ''}" alt="${preview ? this.escape(`${preview.colour} T-shirt preview`) : ''}" width="40" height="40" ${preview ? '' : 'hidden'}>
      </span>`;
    }

    updateEditorPreview(row, catalog, color) {
      const previewNode = row?.querySelector('[data-fleximaa-editor-preview]');
      const image = previewNode?.querySelector('img');
      if (!previewNode || !image) return;
      const preview = this.catalogPreview(catalog, color);
      image.src = preview?.image || '';
      image.alt = preview ? `${preview.colour} T-shirt preview` : '';
      image.hidden = !preview;
    }

    syncEditorSameSize() {
      const sameSizeSelect = this.dialog?.querySelector('#FleximaaPackEditorSameSize');
      const sizeSelects = [...(this.dialog?.querySelectorAll('[data-fleximaa-option="size"]') || [])];
      if (!sameSizeSelect || !sizeSelects.length) return;

      const firstSize = sizeSelects[0].value;
      sameSizeSelect.value = firstSize && sizeSelects.every((select) => select.value === firstSize) ? firstSize : '';
    }

    validateEditor(state) {
      let valid = true;
      this.dialog.querySelectorAll('[data-fleximaa-editor-item]').forEach((row) => {
        const color = row.querySelector('[data-fleximaa-option="color"]').value;
        const size = row.querySelector('[data-fleximaa-option="size"]').value;
        const variant = this.findVariant(state, color, size);
        const error = row.querySelector('p');
        row.classList.remove('has-error');
        error.hidden = true;

        if (!color || !size) valid = false;
        else if (!variant || !variant.available || !variant.sku) {
          valid = false;
          row.classList.add('has-error');
          error.textContent = !variant ? 'This combination is unavailable.' : !variant.available ? 'This combination is sold out.' : 'This selection has no SKU.';
          error.hidden = false;
        }
      });

      this.dialog.querySelector('[data-fleximaa-pack-update]').disabled = !valid || this.busy;
      return valid;
    }

    findVariant(state, color, size) {
      const product = state.catalog.find((candidate) => candidate.colour === color);
      if (!product) return null;
      const variant = product.variants.find((candidate) => candidate.options[product.sizeIndex] === size);
      return variant ? { ...variant, colour: product.colour, sourceHandle: product.handle } : null;
    }

    async updatePack(state) {
      if (this.busy || !this.validateEditor(state)) return;

      const selections = [...this.dialog.querySelectorAll('[data-fleximaa-editor-item]')].map((row) => ({
        color: row.querySelector('[data-fleximaa-option="color"]').value,
        size: row.querySelector('[data-fleximaa-option="size"]').value
      }));
      const selectedVariants = selections.map((selection) => this.findVariant(state, selection.color, selection.size));
      const newItems = this.buildItems(
        selectedVariants,
        state.group.id,
        state.group.pieces,
        state.builderHandle,
        state.builderTitle
      );
      const oldItems = state.group.items.map((item) => ({
        id: item.variant_id,
        quantity: item.quantity,
        properties: item.properties
      }));

      this.setEditorBusy(true);
      try {
        await this.removeGroupLines(state.group);
        await this.addItems(newItems);
        window.location.reload();
      } catch (error) {
        try {
          await this.addItems(oldItems);
        } catch (restoreError) {
          this.showEditorError('The pack could not be updated and Shopify could not restore it automatically. Please refresh the cart before checkout.');
          this.setEditorBusy(false);
          return;
        }
        this.showEditorError(`${error.message || 'The pack could not be updated.'} Your original pack has been restored.`);
        this.setEditorBusy(false);
      }
    }

    buildItems(variants, packId, pieces, builderHandle, builderTitle) {
      const grouped = new Map();
      variants.forEach((variant) => {
        const key = Number(variant.id);
        const existing = grouped.get(key) || { variant, quantity: 0 };
        existing.quantity += 1;
        grouped.set(key, existing);
      });
      return [...grouped.entries()].map(([id, entry]) => ({
        id,
        quantity: entry.quantity,
        properties: {
          [PACK_ID]: packId,
          [PACK_SIZE]: `${pieces} pieces`,
          [SOURCE_HANDLE]: entry.variant.sourceHandle,
          [COLOUR]: entry.variant.colour,
          [BUILDER_HANDLE]: builderHandle,
          [BUILDER_TITLE]: builderTitle
        }
      }));
    }

    async removePack(packId) {
      const group = this.groups.get(packId);
      if (!group || this.busy||this.removePending) return;
      const offer=group.items.every(item=>item.properties?.[BUILDER_HANDLE]==='all-offers');
      this.removePending=true;
      let confirmed;
      try{confirmed=offer?await this.confirmOfferRemoval(group):window.confirm(`Remove the complete ${group.pieces}-piece pack from your cart?`);}
      finally{this.removePending=false;}
      if(!confirmed)return;

      this.busy = true;
      try {
        await this.removeGroupLines(group);
        window.location.reload();
      } catch (error) {
        this.busy = false;
        this.showNotice('The pack could not be removed. Please refresh and try again.');
      }
    }

    confirmOfferRemoval(group){
      return new Promise(resolve=>{
        const dialog=document.createElement('dialog');dialog.className='fa-offer-cart-remove';dialog.setAttribute('aria-labelledby','OfferRemoveTitle');
        dialog.innerHTML=`<h2 id="OfferRemoveTitle">Remove this pack?</h2><p>All ${group.pieces} pieces will be removed from your cart.</p><div><button type="button" data-keep-offer>Keep pack</button><button type="button" data-remove-offer>Remove pack</button></div>`;
        let confirmed=false;dialog.querySelector('[data-keep-offer]').addEventListener('click',()=>dialog.close());dialog.querySelector('[data-remove-offer]').addEventListener('click',()=>{confirmed=true;dialog.close();});
        dialog.addEventListener('close',()=>{dialog.remove();resolve(confirmed);},{once:true});document.body.append(dialog);dialog.showModal();
      });
    }

    removeGroupLines(group) {
      const updates = {};
      group.items.forEach((item) => { updates[item.key] = 0; });
      return this.request('cart/update.js', { method: 'POST', body: JSON.stringify({ updates }) });
    }

    addItems(items) {
      return this.request('cart/add.js', { method: 'POST', body: JSON.stringify({ items }) });
    }

    setEditorBusy(busy) {
      this.busy = busy;
      const button = this.dialog?.querySelector('[data-fleximaa-pack-update]');
      if (button) {
        button.disabled = busy;
        button.textContent = busy ? 'Updating…' : 'Update pack';
      }
    }

    showEditorError(message) {
      const error = this.dialog?.querySelector('.fleximaa-pack-editor__error');
      if (!error) return;
      error.textContent = message;
      error.hidden = false;
    }

    showNotice(message) {
      window.alert(message);
    }

    handleFromUrl(url) {
      return String(url || '').match(/\/products\/([^/?#]+)/)?.[1] || '';
    }

    displayColour(colour) {
      return String(colour || '').trim() === 'Pakistan Green' ? 'Green' : colour;
    }

    async request(path, options = {}) {
      const response = await fetch(`${ROOT}${path}`, {
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        ...options
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'Shopify could not complete this request.');
      return result;
    }

    async requestText(path) {
      const response = await fetch(`${ROOT}${path}`, { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('Shopify could not load the pack catalogue.');
      return response.text();
    }

    escape(value) {
      return String(value).replace(/[&<>'"]/g, (character) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
      })[character]);
    }
  }

  const start = () => new FleximaaPackCartManager();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();

